import { criterion, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { MAKRO_GRAFIKEN } from "../makros";

export const lektionSmartArt: Lesson = {
  id: "smartart-diagramme",
  title: "SmartArt und Diagramme",
  summary: "Abläufe und Hierarchien als Grafik, Zahlen als Diagramm – direkt in Word.",
  durationMinutes: 9,
  learningGoals: [
    "Eine SmartArt-Grafik für einen Ablauf oder eine Hierarchie einsetzen",
    "Ein Diagramm in Word einfügen und mit einem Aussagetitel versehen",
  ],
  steps: [
    {
      id: "smartart",
      title: "SmartArt für Abläufe und Hierarchien",
      explanation: [
        "„Einfügen“ → „SmartArt“ bietet fertige Grafiken für Prozesse, Zyklen, Hierarchien " +
          "und Beziehungen. Der Text wird im Textbereich links neben der Grafik eingegeben – " +
          "wie eine Aufzählung mit Ebenen. Word verteilt und formatiert die Formen selbst.",
        "Das ist der Vorteil gegenüber selbst gezeichneten Kästchen und Pfeilen: Kommt ein " +
          "Schritt dazu, genügt eine neue Zeile im Textbereich, und alles ordnet sich neu. " +
          "Selbst gezeichnete Grafiken müssen von Hand verschoben werden.",
        "SmartArt-Grafiken brauchen wie Bilder einen Alternativtext. Screenreader lesen " +
          "den Text der Formen zwar vor, aber ohne den Zusammenhang, den die Pfeile zeigen.",
      ],
      keyPoints: [
        "Einfügen → SmartArt → Prozess, Hierarchie, Zyklus …",
        "Text im Textbereich eingeben, nicht in einzelne Formen.",
        "Alternativtext beschreibt den Zusammenhang.",
      ],
      task: {
        instruction:
          "Füge eine SmartArt-Grafik mit mindestens drei Elementen ein, etwa einen Ablauf " +
          "„Planen → Umsetzen → Prüfen“, und gib ihr einen Alternativtext.",
        hint: "Einfügen → SmartArt → Prozess → „Einfacher Prozess“. Alternativtext: Rechtsklick auf den Rahmen → Alternativtext anzeigen.",
        macro: MAKRO_GRAFIKEN,
        check: () =>
          pruefeMitMakro(MAKRO_GRAFIKEN, (bericht) => {
            const anzahl = bericht.zahl("smartart") ?? 0;
            const knoten = bericht.zahl("smartartKnoten") ?? 0;
            const mitAlt = bericht.zahl("smartartMitAlternativtext") ?? 0;
            return fromCriteria(
              [
                criterion(`Eine SmartArt-Grafik ist eingefügt (gefunden: ${anzahl})`, anzahl > 0),
                criterion(`Sie hat mindestens drei Elemente (gefunden: ${knoten})`, knoten >= 3),
                criterion("Sie hat einen Alternativtext", mitAlt > 0),
              ],
              {
                success: "Ein Ablauf, der sich selbst neu ordnet – und auch vorgelesen verständlich ist.",
                failure: "Die SmartArt-Grafik ist noch nicht vollständig.",
                hint: "Selbst gezeichnete Formen zählen nicht – nutze Einfügen → SmartArt.",
              },
            );
          }),
      },
    },
    {
      id: "diagramm",
      title: "Ein Diagramm in Word",
      explanation: [
        "„Einfügen“ → „Diagramm“ legt ein Diagramm an und öffnet dazu ein kleines " +
          "Excel-Fenster mit Beispieldaten. Die Daten werden im Word-Dokument gespeichert; " +
          "„Daten bearbeiten“ öffnet sie später wieder.",
        "Für den Diagrammtyp und den Titel gilt, was im Excel- und im PowerPoint-Kurs steht: " +
          "Der Typ folgt der Frage, und der Titel sagt die Aussage – „Umsatz 2025 um 12 % " +
          "gestiegen“ statt „Umsatz“.",
        "Stammen die Zahlen aus einer vorhandenen Excel-Datei, ist Kopieren und " +
          "„Einfügen“ → „Verknüpfen“ die Alternative: Dann aktualisiert sich das Diagramm, " +
          "wenn sich die Excel-Datei ändert.",
      ],
      keyPoints: [
        "Einfügen → Diagramm – die Daten liegen im Dokument.",
        "Aussagetitel statt Kategorie.",
        "Verknüpft einfügen, wenn die Excel-Datei führend ist.",
      ],
      task: {
        instruction: "Füge ein Diagramm ein und gib ihm einen Titel, der eine Aussage macht.",
        hint: "Einfügen → Diagramm → Säule, dann in den Titel klicken und überschreiben.",
        macro: MAKRO_GRAFIKEN,
        check: () =>
          pruefeMitMakro(MAKRO_GRAFIKEN, (bericht) => {
            const diagramme = bericht.zahl("diagramme") ?? 0;
            const titel = (bericht.text("diagrammtitel") ?? "").trim();
            const aussage = titel.split(/\s+/).filter(Boolean).length >= 3;
            return fromCriteria(
              [
                criterion(`Ein Diagramm ist eingefügt (gefunden: ${diagramme})`, diagramme > 0),
                criterion(`Es hat einen Aussagetitel mit mindestens drei Wörtern (${titel || "kein Titel"})`, aussage),
              ],
              {
                success: "Diagramm mit Aussage – der Titel sagt, was die Zahlen zeigen.",
                failure: "Diagramm oder Aussagetitel fehlen noch.",
                hint: "„Diagrammtitel“ oder „Umsatz“ ist eine Kategorie, keine Aussage.",
              },
            );
          }),
      },
    },
  ],
};
